import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// ── Icons ────────────────────────────────────────────────────────────────────
const IconPlus    = ({ size = 16 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const IconEdit    = ({ size = 14 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
const IconTrash   = ({ size = 14 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>;
const IconToggle  = ({ size = 14 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><rect x="1" y="5" width="22" height="14" rx="7"/><circle cx="16" cy="12" r="3" fill="currentColor"/></svg>;
const IconClose   = ({ size = 18 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const IconWifi    = ({ size = 16 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><circle cx="12" cy="20" r="1" fill="currentColor"/></svg>;
const IconSpeed   = ({ size = 14 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>;

// ── Helpers ──────────────────────────────────────────────────────────────────
function fmtDuration(mins) {
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) return `${mins / 60}h`;
  const d = mins / 1440;
  return d % 1 === 0 ? `${d}d` : `${(mins / 60).toFixed(1)}h`;
}

function fmtData(mb) {
  if (!mb || mb === 0) return 'Unlimited';
  if (mb < 1024) return `${mb} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
}

function fmtSpeed(limit, unit) {
  if (!limit || limit === 0) return 'Unlimited';
  return `${limit} ${unit === 1 ? 'Kbps' : 'Mbps'}`;
}

const EMPTY_FORM = {
  name: '', price: '', duration_mins: '',
  data_limit_mb: '', speed_down_limit: '', speed_down_unit: 2,
  addon_price: '', description: '',
};

// ── Badge ────────────────────────────────────────────────────────────────────
function Badge({ active }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
      textTransform: 'uppercase', padding: '3px 8px', borderRadius: 20,
      background: active ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.1)',
      color: active ? '#22c55e' : 'var(--red)',
    }}>{active ? 'Active' : 'Inactive'}</span>
  );
}

// ── Modal ────────────────────────────────────────────────────────────────────
function Modal({ title, onClose, children }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 500,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
      padding: 16,
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 14, width: '100%', maxWidth: 520,
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 24px 60px rgba(0,0,0,0.4)',
        animation: 'modalIn 0.18s ease',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 22px 16px', borderBottom: '1px solid var(--border)',
        }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, fontFamily: 'Syne, sans-serif', color: 'var(--text)' }}>{title}</h3>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text3)', display: 'flex', padding: 4, borderRadius: 6,
          }}><IconClose /></button>
        </div>
        <div style={{ padding: '20px 22px' }}>{children}</div>
      </div>
      <style>{`@keyframes modalIn { from { opacity:0; transform:translateY(10px) scale(0.98); } to { opacity:1; transform:none; } }`}</style>
    </div>
  );
}

// ── Form Field ───────────────────────────────────────────────────────────────
function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>
        {label}{hint && <span style={{ fontWeight: 400, color: 'var(--text3)', marginLeft: 6 }}>{hint}</span>}
      </label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '9px 12px', borderRadius: 8,
  border: '1px solid var(--border2)', background: 'var(--bg)',
  color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
  outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s',
};

// ── Plan Form ────────────────────────────────────────────────────────────────
function PlanForm({ initial = EMPTY_FORM, onSubmit, loading, onCancel }) {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      name:             form.name.trim(),
      price:            parseFloat(form.price),
      duration_mins:    parseInt(form.duration_mins, 10),
      data_limit_mb:    form.data_limit_mb !== '' ? parseInt(form.data_limit_mb, 10) : 0,
      speed_down_limit: form.speed_down_limit !== '' ? parseInt(form.speed_down_limit, 10) : 0,
      speed_down_unit:  parseInt(form.speed_down_unit, 10),
      addon_price:      form.addon_price !== '' ? parseFloat(form.addon_price) : 0,
      description:      form.description.trim(),
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Plan Name">
            <input style={inputStyle} value={form.name} onChange={set('name')} required placeholder="e.g. Basic Daily" />
          </Field>
        </div>
        <Field label="Price" hint="(₦)">
          <input style={inputStyle} type="number" min="0" step="0.01" value={form.price} onChange={set('price')} required placeholder="500" />
        </Field>
        <Field label="Duration" hint="(minutes)">
          <input style={inputStyle} type="number" min="1" value={form.duration_mins} onChange={set('duration_mins')} required placeholder="1440 = 1 day" />
        </Field>
        <Field label="Data Limit" hint="(MB, 0 = unlimited)">
          <input style={inputStyle} type="number" min="0" value={form.data_limit_mb} onChange={set('data_limit_mb')} placeholder="0" />
        </Field>
        <Field label="Add-on Price" hint="(₦ per extra device)">
          <input style={inputStyle} type="number" min="0" step="0.01" value={form.addon_price} onChange={set('addon_price')} placeholder="0" />
        </Field>
        <Field label="Speed Limit" hint="(0 = unlimited)">
          <input style={inputStyle} type="number" min="0" value={form.speed_down_limit} onChange={set('speed_down_limit')} placeholder="0" />
        </Field>
        <Field label="Speed Unit">
          <select style={inputStyle} value={form.speed_down_unit} onChange={set('speed_down_unit')}>
            <option value={1}>Kbps</option>
            <option value={2}>Mbps</option>
          </select>
        </Field>
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Description" hint="(optional)">
            <textarea
              style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }}
              value={form.description} onChange={set('description')}
              placeholder="Short plan description shown to users…"
            />
          </Field>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
        <button type="button" onClick={onCancel}
          style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid var(--border2)', background: 'transparent', color: 'var(--text2)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
          Cancel
        </button>
        <button type="submit" disabled={loading}
          style={{ padding: '9px 22px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: loading ? 0.7 : 1 }}>
          {loading ? 'Saving…' : 'Save Plan'}
        </button>
      </div>
    </form>
  );
}

// ── Confirm Dialog ───────────────────────────────────────────────────────────
function Confirm({ message, onConfirm, onCancel, loading }) {
  return (
    <Modal title="Confirm Action" onClose={onCancel}>
      <p style={{ color: 'var(--text2)', fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>{message}</p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button onClick={onCancel}
          style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid var(--border2)', background: 'transparent', color: 'var(--text2)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
          Cancel
        </button>
        <button onClick={onConfirm} disabled={loading}
          style={{ padding: '9px 22px', borderRadius: 8, border: 'none', background: 'var(--red)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: loading ? 0.7 : 1 }}>
          {loading ? 'Processing…' : 'Confirm'}
        </button>
      </div>
    </Modal>
  );
}

// ── Toast ────────────────────────────────────────────────────────────────────
function Toast({ toasts }) {
  return (
    <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 900, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {toasts.map(t => (
        <div key={t.id} style={{
          padding: '11px 18px', borderRadius: 10, fontSize: 13, fontWeight: 500,
          background: t.type === 'error' ? 'var(--red)' : '#22c55e',
          color: '#fff', boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
          animation: 'slideUp 0.2s ease',
          maxWidth: 320,
        }}>{t.message}</div>
      ))}
      <style>{`@keyframes slideUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:none; } }`}</style>
    </div>
  );
}

// ── Plan Card ────────────────────────────────────────────────────────────────
function PlanCard({ plan, onEdit, onDelete, onToggle }) {
  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 12, padding: '18px 20px',
      opacity: plan.is_active ? 1 : 0.6,
      transition: 'box-shadow 0.15s, opacity 0.2s',
      display: 'flex', flexDirection: 'column', gap: 12,
    }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.12)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
    >
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, fontFamily: 'Syne, sans-serif', color: 'var(--text)', marginBottom: 4 }}>
            {plan.name}
          </div>
          <Badge active={plan.is_active} />
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 20, fontWeight: 800, fontFamily: 'Syne, sans-serif', color: 'var(--accent)' }}>
            ₦{Number(plan.price).toLocaleString()}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
            {fmtDuration(plan.duration_mins)}
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {[
          { label: 'Data', value: fmtData(plan.data_limit_mb) },
          { label: 'Speed', value: fmtSpeed(plan.speed_down_limit, plan.speed_down_unit) },
          { label: 'Add-on', value: plan.addon_price > 0 ? `₦${Number(plan.addon_price).toLocaleString()}` : 'None' },
        ].map(({ label, value }) => (
          <div key={label} style={{
            flex: 1, minWidth: 70, background: 'var(--bg)',
            borderRadius: 8, padding: '7px 10px',
          }}>
            <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>{label}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{value}</div>
          </div>
        ))}
      </div>

      {plan.description && (
        <p style={{ margin: 0, fontSize: 12, color: 'var(--text3)', lineHeight: 1.5 }}>{plan.description}</p>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, paddingTop: 4, borderTop: '1px solid var(--border)' }}>
        <ActionBtn icon={<IconEdit />} label="Edit" onClick={() => onEdit(plan)} />
        <ActionBtn
          icon={<IconToggle />}
          label={plan.is_active ? 'Deactivate' : 'Activate'}
          onClick={() => onToggle(plan)}
          color={plan.is_active ? 'var(--text3)' : 'var(--accent)'}
        />
        <ActionBtn icon={<IconTrash />} label="Delete" onClick={() => onDelete(plan)} danger />
      </div>
    </div>
  );
}

function ActionBtn({ icon, label, onClick, danger, color }) {
  const [hover, setHover] = useState(false);
  const base = {
    display: 'flex', alignItems: 'center', gap: 5,
    padding: '6px 11px', borderRadius: 7, border: '1px solid var(--border2)',
    background: 'transparent', fontSize: 12, cursor: 'pointer',
    fontFamily: 'inherit', transition: 'all 0.12s',
    color: color || (danger ? 'var(--red)' : 'var(--text2)'),
  };
  const hovered = danger
    ? { background: 'var(--red-dim)', borderColor: 'rgba(239,68,68,0.35)' }
    : { background: 'var(--accent-dim)', borderColor: 'var(--accent)', color: 'var(--accent)' };

  return (
    <button
      onClick={onClick}
      style={{ ...base, ...(hover ? hovered : {}) }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {icon}{label}
    </button>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function Plans() {
  const [plans, setPlans]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(null); // 'create' | 'edit' | 'delete' | 'toggle'
  const [target, setTarget]     = useState(null);
  const [saving, setSaving]     = useState(false);
  const [toasts, setToasts]     = useState([]);
  const [filter, setFilter]     = useState('all'); // 'all' | 'active' | 'inactive'

  const toast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(t => [...t, { id, message, type }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3500);
  };

  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const { plans } = await api.getPlans?.() || await fetch('/api/admin/plans').then(r => r.json());
      setPlans(plans || []);
    } catch (err) {
      toast(err.message || 'Failed to load plans', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPlans(); }, [loadPlans]);

  const handleCreate = async (body) => {
    setSaving(true);
    try {
      await api.createPlan?.(body) || await fetch('/api/admin/plans', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      toast('Plan created successfully');
      setModal(null);
      loadPlans();
    } catch (err) {
      toast(err.message || 'Failed to create plan', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (body) => {
    setSaving(true);
    try {
      await api.updatePlan?.(target.id, body) || await fetch(`/api/admin/plans/${target.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      toast('Plan updated successfully');
      setModal(null);
      loadPlans();
    } catch (err) {
      toast(err.message || 'Failed to update plan', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await api.deletePlan?.(target.id) || await fetch(`/api/admin/plans/${target.id}`, { method: 'DELETE' });
      toast('Plan deleted');
      setModal(null);
      setTarget(null);
      loadPlans();
    } catch (err) {
      toast(err.message || 'Failed to delete plan', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async () => {
    setSaving(true);
    try {
      await api.togglePlan?.(target.id) || await fetch(`/api/admin/plans/${target.id}/toggle`, { method: 'PATCH' });
      toast(`Plan ${target.is_active ? 'deactivated' : 'activated'}`);
      setModal(null);
      setTarget(null);
      loadPlans();
    } catch (err) {
      toast(err.message || 'Failed to toggle plan', 'error');
    } finally {
      setSaving(false);
    }
  };

  const filtered = plans.filter(p =>
    filter === 'all' ? true : filter === 'active' ? p.is_active : !p.is_active
  );

  const activeCnt   = plans.filter(p => p.is_active).length;
  const inactiveCnt = plans.length - activeCnt;

  return (
    <>
      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, fontFamily: 'Syne, sans-serif', color: 'var(--text)' }}>Plans</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text3)' }}>Manage subscription plans available to users</p>
        </div>
        <button
          onClick={() => setModal('create')}
          style={{
            display: 'flex', alignItems: 'center', gap: 7,
            padding: '9px 18px', borderRadius: 9, border: 'none',
            background: 'var(--accent)', color: '#fff',
            fontSize: 13, fontWeight: 600, cursor: 'pointer',
            fontFamily: 'inherit', transition: 'opacity 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
        >
          <IconPlus /> New Plan
        </button>
      </div>

      {/* Stats strip */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { label: 'Total Plans', value: plans.length, color: 'var(--accent)' },
          { label: 'Active', value: activeCnt, color: '#22c55e' },
          { label: 'Inactive', value: inactiveCnt, color: 'var(--red)' },
        ].map(({ label, value, color }) => (
          <div key={label} style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 10, padding: '12px 18px', minWidth: 110,
          }}>
            <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, fontFamily: 'Syne, sans-serif', color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
        {['all', 'active', 'inactive'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            style={{
              padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
              border: '1px solid', cursor: 'pointer', fontFamily: 'inherit',
              textTransform: 'capitalize', transition: 'all 0.12s',
              background: filter === f ? 'var(--accent)' : 'transparent',
              color: filter === f ? '#fff' : 'var(--text3)',
              borderColor: filter === f ? 'var(--accent)' : 'var(--border2)',
            }}>
            {f}
          </button>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {[1, 2, 3].map(i => (
            <div key={i} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, height: 200, animation: 'pulse 1.4s ease-in-out infinite' }} />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '60px 20px',
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 12,
        }}>
          <div style={{ marginBottom: 12, opacity: 0.3 }}><IconWifi size={40} /></div>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>No plans found</div>
          <div style={{ fontSize: 13, color: 'var(--text3)' }}>
            {filter !== 'all' ? `No ${filter} plans.` : 'Create your first plan to get started.'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {filtered.map(plan => (
            <PlanCard
              key={plan.id}
              plan={plan}
              onEdit={(p) => { setTarget(p); setModal('edit'); }}
              onDelete={(p) => { setTarget(p); setModal('delete'); }}
              onToggle={(p) => { setTarget(p); setModal('toggle'); }}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {modal === 'create' && (
        <Modal title="Create New Plan" onClose={() => setModal(null)}>
          <PlanForm
            onSubmit={handleCreate}
            loading={saving}
            onCancel={() => setModal(null)}
          />
        </Modal>
      )}

      {modal === 'edit' && target && (
        <Modal title={`Edit — ${target.name}`} onClose={() => { setModal(null); setTarget(null); }}>
          <PlanForm
            initial={{
              name:             target.name,
              price:            target.price,
              duration_mins:    target.duration_mins,
              data_limit_mb:    target.data_limit_mb ?? '',
              speed_down_limit: target.speed_down_limit ?? '',
              speed_down_unit:  target.speed_down_unit ?? 2,
              addon_price:      target.addon_price ?? '',
              description:      target.description ?? '',
            }}
            onSubmit={handleEdit}
            loading={saving}
            onCancel={() => { setModal(null); setTarget(null); }}
          />
        </Modal>
      )}

      {modal === 'delete' && target && (
        <Confirm
          message={`Are you sure you want to permanently delete "${target.name}"? This action cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => { setModal(null); setTarget(null); }}
          loading={saving}
        />
      )}

      {modal === 'toggle' && target && (
        <Confirm
          message={`${target.is_active ? 'Deactivate' : 'Activate'} "${target.name}"? ${target.is_active ? 'Users will no longer see this plan.' : 'This plan will become visible to users.'}`}
          onConfirm={handleToggle}
          onCancel={() => { setModal(null); setTarget(null); }}
          loading={saving}
        />
      )}

      <Toast toasts={toasts} />

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </>
  );
}